import type { JSONSchema7Definition } from 'json-schema';
import type {
  Schedule,
  CarrierAccount,
  CarrierSetting,
  Contract,
  CarrierAccountCredentialDefinition,
  FulfillmentContextLinkProperty,
  LabelAugmentationSet,
  ReturnsSettings,
  ThirdPartyBillingAccount,
  Packaging,
  PackagingSet,
  LabelProperties,
} from '../assets/types';
import type { FormInstance } from 'antd';
import type { EditAssetWorkflow } from './EditAssetWorkflow';
import type {
  CarrierCarrierInfo,
  CarrierServiceMethodInfo,
  PartnerAddressV2,
  PartnerCarrierRateSet,
  PartnerContact,
  PartnerLaneSortConfigurationOrchestration,
  PartnerOrigin,
  PartnerRateTable,
  PartnerTag,
  PartnerTenant,
} from '@shipium-corp/shipium-ts-types';

// alphanumeric chars of any casing + hyphens + underscores (no casing)
export const PartnerProvidedIDValidationRegex = new RegExp('^[a-zA-Z0-9_-]+$');
export const InvalidPartnerProvidedIDMessage = 'Identifier does not match the required format';
export type WorkflowStateToAssetMappingTypes =
  | 'property'
  | 'nestedProperty'
  | 'link'
  | 'unlink'
  | 'ignore'
  | 'properties';

interface BaseWorkflowStateToAssetMapping {
  mappingType: WorkflowStateToAssetMappingTypes;
  workflowStateValueOverride?: (workFlowState: any) => any;
}

export type WorkflowStateToAssetMapping =
  | WorkflowStateToAssetIgnoreMapping
  | WorkflowStateToAssetLinkMapping
  | WorkflowStateToAssetPropertyMapping
  | WorkflowStateToAssetNestedPropertyMapping
  | WorkflowStateToAssetPropertiesMapping;

export interface WorkflowStateToAssetIgnoreMapping extends BaseWorkflowStateToAssetMapping {
  mappingType: 'ignore';
}
export type AssetLinkType = 'Tenant' | 'Standard';

export interface WorkflowStateToAssetLinkMapping extends BaseWorkflowStateToAssetMapping {
  mappingType: 'link';
  list?: boolean;
  linkAssetTypeOverride?: string;
  excludeFromInitalState?: boolean;
  linkAssetType: AssetTypes;
  linkType?: AssetLinkType;
}

export interface WorkflowStateToAssetPropertyMapping extends BaseWorkflowStateToAssetMapping {
  mappingType: 'property';
  excludeFromInitalState?: boolean;
  property: string;
}

export interface WorkflowStateToAssetPropertiesMapping extends BaseWorkflowStateToAssetMapping {
  mappingType: 'properties';
  excludeFromInitalState?: boolean;
  properties: string[];
}

export interface WorkflowStateToAssetNestedPropertyMapping extends BaseWorkflowStateToAssetMapping {
  mappingType: 'nestedProperty';
  excludeFromInitalState?: boolean;
  parent: string;
  property: string;
}

export interface AssetActionPayloadLink {
  assetType: AssetTypes;
  assetId: string | string[];
  linkAssetTypeOverride?: string;
  unlink?: boolean;
  linkType?: AssetLinkType;
}
export type AssetActionType = 'add' | 'update' | 'delete';

export interface AssetActionPayload {
  assetActionType: AssetActionType;
  assetId?: string;
  assetType: AssetTypes;
  assetPayload?: Record<string, any>;
  assetLinks: AssetActionPayloadLink[];
  overrideSave?: OverrideSaveTypes;
}

export interface AssetActionPayloadResult extends AssetActionPayload {
  assetResult?: BaseAsset;
  error?: WorkflowActionError;
}

export interface AuditInfo {
  createdBy: string;
  creationTimeStamp: string;
  lastUpdatedBy: string;
  lastUpdatedTimeStamp: string;
  version: number;
}

export interface BaseAsset {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  name: string;
  description: string;
  tagIds: string[];
  urn: string;
}

export interface WorkflowGroupOptions {
  workflowGroupKey: string;
  workflowGroupName: string;
  workflowGroupIcon: string;
  workflowGroupDescription: string;
  workflowGroupIndex: number;
  excludeFromConfigurationIndex?: boolean;
  required: boolean;
  workflowGroupCategory: WorkflowGroupCategory;
  workflowGroupWorkflowOptions: WorkflowOptions[];
  shipiumOnly?: boolean;
  requiredFeatures?: Record<string, string>;
  shipiumOnlyFeatureToggleRestricted?: boolean;
}

export type WorkflowGroupCategory = 'fulfillmentCenter' | 'carrier' | 'label' | 'shiptime' | 'packaging';
export type WorkflowType = 'configurationWorkflow' | 'addAssetWorkflow' | 'editAssetWorkflow';

export interface WorkflowOptions {
  workflowKey: string;
  workflowName: string;
  workflowActionIcon?: string;
  workflowDescription?: string;
  workflowHelp?: string;
  workflowIndex: number;
  workflowType: WorkflowType;
  // a list of asset types to fetch upon loading this workflow
  workflowAssets?: AssetTypes[];
  workflowStepOptions: WorkflowStepOptions[];
  // used to develop optional workflows
  workflowConditionalMatcher?: ConditionalMatcher[];
  hideIfDisabled?: boolean;
  shipiumOnly?: boolean;
  workflowStateTransformFunction?: (val: any, asset?: Asset) => any;
}

export interface AssetWorkflowOptions extends WorkflowOptions {
  assetType: AssetTypes;
  workflowStepOptions: AssetWorkflowStepOptions[];
  deleteAction?: boolean;
  overrideSave?: OverrideSaveTypes;
}

export interface EditAssetWorkflowOptions extends AssetWorkflowOptions {
  assetType: AssetTypes;
  workflowStepOptions: AssetWorkflowStepOptions[];
  assetSchemaMatcher?: JSONSchema7Definition;
}

export type AssetTypes =
  | 'carrier'
  | 'carrierServiceMethod'
  | 'origin'
  | 'tags'
  | 'address'
  | 'contact'
  | 'schedule'
  | 'carrierAccount'
  | 'carrierRate'
  | 'returnsSettings'
  | 'labelAugmentation'
  | 'packaging'
  | 'packagingSet'
  | 'labelProperties'
  | 'fulfillmentContext'
  | 'carrierSettings'
  | 'contract'
  | 'carrierRateSet'
  | 'returnAddressOverride'
  | 'fulfillmentContextLinkProperty'
  | 'augmentationSet'
  | 'tenant'
  | 'thirdPartyBillingAccount'
  | 'thirdPartyBillingAccountSet'
  | 'laneSortConfigurations';
export type Asset = PartnerOrigin | PartnerTag | PartnerAddressV2 | PartnerContact | Schedule | CarrierAccount;

export interface WorkflowAsset {
  assetId: string;
  assetType: AssetTypes;
  assetIdKey: string;
  active: boolean;
  archived: boolean;
  workflow: EditAssetWorkflow;
}

export interface ConditionalAssetWorkflowStepMatcher {
  workflowKey?: string;
  workflowStepKey: string;
  generateSchema: (assetState: AssetState) => JSONSchema7Definition;
}

export interface ConditionalWorkflowStepMatcher {
  workflowKey?: string;
  workflowStepKey: string;
  schema: JSONSchema7Definition;
}

export interface ConditionalAssetMatcher {
  assetType: AssetTypes;
  assetId?: string;
  schema: JSONSchema7Definition;
}

export type ConditionalOrMatcher = {
  _or: ConditionalMatcher[];
};

export type ConditionalAndMatcher = {
  _and: ConditionalMatcher[];
};

export type ConditionalMatcher =
  | ConditionalAssetWorkflowStepMatcher
  | ConditionalWorkflowStepMatcher
  | ConditionalAssetMatcher
  | ConditionalOrMatcher
  | ConditionalAndMatcher;

export interface IncludeInDefaultAssetNameRules {
  includeInDefaultAssetName?: boolean;
  prefix?: string;
  suffix?: string;
  index?: number;
}

export interface WorkflowStepOptions {
  shipiumOnly?: boolean;
  //indicate that step is skippable
  optional?: boolean;
  // An identifier to be used by other workflow step predicates
  workflowStepKey: string;
  // A friendly name to be displayed in the workflow navigation menu
  workflowStepNavigationName: string;
  // An ordinal to be used when constructing the chain of responcibility (the most complete steps should be higher)
  workflowStepIndex?: number;

  calculatedComponentProps?: Record<string, CalculatedComponentProps>;
  // a workflow component to be rendered when this step is selected by the CoR
  workflowComponent: WorkflowComponent;
  // always exclude this step from navitation
  excludeFromNavigation?: boolean;
  // always exclude this step from navitation
  excludeFromWorkflowStatus?: boolean;
  // refresh assets when completing this step
  refreshAssetsOnStepCompletion?: boolean;
  // conditional matcher used to create optional workflow steps
  workflowStepConditionalMatcher?: ConditionalMatcher[];
  // optional rules to format the default asset name
  includeInDefaultAssetNameRules?: IncludeInDefaultAssetNameRules;
}

export interface CalculatedComponentProps {
  type: 'asset' | 'workflowStep';
  property: string;
  transformFn?: (val: any) => any;
}

export interface AssetToWorkflowStateMapping {
  assetProperties?: string[];
  assetProperty?: string;
  computeFn?: (assetProperty: any) => any;
}

export interface AssetWorkflowStepOptions extends WorkflowStepOptions {
  markComplete?: boolean;
  workflowStateToAssetMapping: WorkflowStateToAssetMapping;
  assetToWorkflowStateMapping?: AssetToWorkflowStateMapping;
  shipiumOnly?: boolean;
}

export interface AssetState {
  returnsSettings: ReturnsSettings[];
  origin: PartnerOrigin[];
  address: PartnerAddressV2[];
  contact: PartnerContact[];
  tags: PartnerTag[];
  schedule: any[];
  carrier: CarrierCarrierInfo[];
  carrierServiceMethod: CarrierServiceMethodInfo[];
  carrierAccount: CarrierAccount[];
  carrierRate: any[];
  labelAugmentation: any[];
  packaging: Packaging[];
  packagingSet: PackagingSet[];
  labelProperties: LabelProperties[];
  fulfillmentContext: any[];
  carrierSettings: CarrierSetting[];
  carrierRateSet: PartnerCarrierRateSet[];
  contract: Contract[];
  augmentationSet: LabelAugmentationSet[];
  fulfillmentContextLinkProperty: FulfillmentContextLinkProperty[];
  tenant: PartnerTenant[];
  thirdPartyBillingAccount: ThirdPartyBillingAccount[];
  thirdPartyBillingAccountSet: any[];
  returnAddressOverride: any[];
  laneSortConfigurations: PartnerLaneSortConfigurationOrchestration[];
}

export interface WorkflowContext<WorkflowState = any> {
  workflowKey: string;
  workflowState: WorkflowState;
  workflowAssets: AssetState;
  workflowStepKey?: string;
  workflowFromStepKey?: string;
}
export interface WorkflowComponentProps {
  workflowResponse: WorkflowResponse;
  form: FormInstance;
}

export type WorkflowComponentType =
  | 'singleSelect'
  | 'fedexSmartPostHubSelect'
  | 'multipleSelect'
  | 'addressEntry'
  | 'contactEntry'
  | 'workflowSummary'
  | 'carrierServiceMethodEntry'
  | 'carrierEntry'
  | 'workflowError'
  | 'textEntry'
  | 'tagEntry'
  | 'scheduleEntry'
  | 'credentialEntry'
  | 'carrierDimensionSelect'
  | 'dateEntry'
  | 'scheduleEditEntry'
  | 'carrierAccountSelect'
  | 'carrierRateSetSelect'
  | 'carrierSettingSelect'
  | 'scheduleSelect'
  | 'originSelect'
  | 'contractSelect'
  | 'labelAugmentationSelect'
  | 'assetNameEntry'
  | 'addressLocation'
  | 'zplEntry'
  | 'assetSetSelect'
  | 'returnsSettingSelect'
  | 'nonZplEntry'
  | 'labelPropertiesSelect'
  | 'carrierAugmentationSelect'
  | 'serviceMethodAugmentationSelect'
  | 'cutoffExceptionEntry'
  | 'cutoffTimesEntry'
  | 'supportedDestinationUpload'
  | 'injectionRateModifier'
  | 'scheduleEntrySelect'
  | 'tenantSelect'
  | 'tenantReturnAddressEntry'
  | 'thirdPartyBillingAccountSelect'
  | 'fctxTypeSelect'
  | 'phoneNumberEntry'
  | 'returnToAddressSelect'
  | 'shipmentLaneSelect'
  | 'carrierShipmentLaneConfigurationSelect';

export type WorkflowComponent =
  | WorkflowSingleSelectProps
  | WorkflowMultipleSelectProps
  | WorkflowSummaryProps
  | WorkflowTextEntryProps
  | WorkflowContactEntryProps
  | WorkflowCredentialProps
  | WorkflowAddressEntryProps
  | WorkflowCarrierServiceMethodProps
  | WorkflowRateTableProps
  | WorkflowDateEntryProps
  | WorkflowCarrierSurchargeProps
  | WorkflowScheduleEntryEditProps
  | WorkflowRateTableEditProps
  | WorkflowAssetNameEntryProps
  | WorkflowOriginSelectProps
  | WorkflowAugmentationSetProps
  | WorkflowAssetSetSelectProps
  | WorkflowScheduleEntrySelectProps
  | WorkflowCarrierDimensionSelectProps;

export interface WorkflowDateEntryProps extends WorkflowComponentBase {
  allowPast: boolean;
  allowFuture: boolean;
  dateFormat: 'ISO' | 'UNIX' | string;
  description: string;
  help: string;
  prompt: string;
}

export interface WorkflowCarrierDimensionSelectProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  selectedCarrierId: string;
}

export type DefaultValueFn<T = any> = (workflowState: Record<string, any>, workflowContext: WorkflowContext) => T;

export interface WorkflowComponentBase {
  workflowComponentType: WorkflowComponentType;
  defaultValue?: DefaultValueFn | any;
}

export interface WorkflowOption {
  optionText: string;
  optionValue: string | number | boolean;
  optionHelp?: string;
}

export interface WorkflowNavigationItem {
  workflowStepKey: string;
  workflowStepNavigationName: string;
  workflowStepComplete: boolean;
}

export interface WorkflowContactEntryProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  singleSelect?: boolean;
}

export interface WorkflowScheduleEntrySelectProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  allowMultiple: true;
  scheduleId: string;
}

export type AugmentationLinkType = 'all' | 'carrier' | 'serviceMethod';
export interface WorkflowAugmentationSetProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  allowedLinkTypes: AugmentationLinkType[];
  allowedCarriers: string[];
}

export type OriginLinks = 'address' | 'contact' | 'schedule';

export interface WorkflowOriginSelectProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  requiredLinks: OriginLinks[];
}

export interface WorkflowTenantSelectProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  assetId: string;
}

export interface WorkflowTenantReturnAddressEntryProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
}

export interface WorkflowAddressEntryProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  entryOnly?: boolean;
  addressId?: string;
}

export interface WorkflowCarrierAccountSelectProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  carrierId: string;
}

export interface WorkflowRatingTypeSelectProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  serviceMethodId: string;
}

export interface WorkflowAssetSetSelectProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  assetType: AssetTypes;
  optional?: boolean;
  assetIdProperty: string;
  singleSelect?: boolean;
  assetFilterProperties?: {
    workflowStepKeyToMatch: string;
    assetPropertyToMatch: string;
  };
}

export interface WorkflowCarrierSurchargeProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  carrier: string;
  carrierServiceMethod: string;
}

export type OverrideSaveTypes =
  | 'addAugmentationSet'
  | 'editAugmentationSet'
  | 'editCarrierRateSet'
  | 'addCarrierSchedule'
  | 'deleteCarrierSchedule'
  | 'editThirdPartyBillingAccountSet'
  | 'updatePackagingSetWorkflow'
  | 'editFulfillmentContext';
export interface WorkflowSummaryProps extends WorkflowComponentBase {
  title: string;
  description: string;
  help: string;
}

export interface WorkflowCarrierServiceMethodProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  singleSelect: boolean;
  hideCarriers: boolean;
  hideServiceMethods: boolean;
  allowedCarriers: string[];
  supportsThirdPartyBilling?: boolean;
  allowedServiceMethods: string[];
}

export interface WorkflowRateTableProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  carrier?: string;
  carrierServiceMethodId?: string;
  rateTable: PartnerRateTable['rates'];
}
export interface WorkflowAddressLocationProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  addressId: string;
}

export interface WorkflowRateTableEditProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  carrier?: string;
  carrierServiceMethodId?: string;
  carrierRateId: string;
  dateOnly: boolean;
}

export interface WorkflowSingleSelectProps extends WorkflowComponentBase {
  options: WorkflowOption[];
  prompt: string;
  description: string;
  help: string;
}

export interface WorkflowScheduleEntryEditProps extends WorkflowComponentBase {
  options: WorkflowOption[];
  prompt: string;
  description: string;
  help: string;
  scheduleId: string;
  dateOnly: boolean;
}

export interface WorkflowCredentialProps extends WorkflowComponentBase {
  prompt: string;
  description: string;
  help: string;
  carrierId?: string;
  shipiumCredentials?: boolean;
  credentials: CarrierAccountCredentialDefinition;
  serviceMethodId?: string;
  thirdPartyBilling?: boolean;
}

export interface WorkflowTextEntryProps extends WorkflowComponentBase {
  defaultValue?: DefaultValueFn<string> | string;
  validationRegex?: RegExp;
  validationMessage?: string;
  numeric?: boolean;
  rows?: number;
  prompt: string;
  description: string;
  help: string;
  suffix?: string;
  prefix?: string;
}

export interface WorkflowAssetNameEntryProps extends WorkflowComponentBase {
  validationMessage?: string;
  prompt: string;
  description: string;
  help: string;
}

export interface WorkflowMultipleSelectProps extends WorkflowComponentBase {
  options: WorkflowOption[];
  prompt: string;
  description: string;
  help: string;
}

export interface WorkflowRequestOptions<WorkflowState = any> {
  workflowState: WorkflowState;
  workflowKey: string;
  workflowStepKey?: string;
  workflowFromStepKey?: string;
}

export interface WorkflowRequest {
  workflowKey: string;
  workflowStepKey?: string;
  workflowFromStepKey?: string;
  isShipiumView?: boolean;
}

export interface WorkflowStepResponse {
  workflowStepKey: string;
  workflowComponent?: WorkflowComponent;
  workflowContext: WorkflowContext;
  refreshAssetsOnStepCompletion: boolean;
  optional?: boolean;
}

export interface WorkflowResponse extends WorkflowStepResponse {
  workflowNavigation?: WorkflowNavigationItem[];
  workflowKey: string;
  workflowError?: WorkflowActionError;
  workflowFromStepKey?: string;
}
export type WorkflowActionErrorType = 'serverError' | 'badRequest' | 'timeout' | 'conflict';

export interface WorkflowActionError {
  errorType: WorkflowActionErrorType;
  result: AssetActionPayloadResult;
  message: string;
  stack?: string;
}

export interface WorkflowStepStatus {
  workflowStepKey: string;
  workflowStepNavigationName: string;
  workflowStepPrompt?: string;
  workflowStepValid: boolean;
  workflowStepComplete: boolean;
  workflowStepSkipped: boolean;
  configuredValue?: any;
}

export type WorkflowStatus = 'notStarted' | 'inProgress' | 'completed' | 'disabled';

export interface WorkflowStatusResponse {
  workflowStatus: WorkflowStatus;
  workflowHidden: boolean;
  workflowStepStatus: WorkflowStepStatus[];
}

export interface WorkflowGroupStatusResponse {
  workflowGroupKey: string;
  workflowGroupStatus: WorkflowStatus;
  notStarted: WorkflowStatusResponse[];
  inProgress: WorkflowStatusResponse[];
  completed: WorkflowStatusResponse[];
  disabled: WorkflowStatusResponse[];
}

export type AssetSummaryFormat =
  | 'descriptionItem'
  | 'descriptionList'
  | 'detail'
  | 'tag'
  | 'text'
  | 'card'
  | 'innerCard';
